// ================================================================
// 📊 EXECUTIVE DASHBOARD - Dashboard Executivo (Visão 360°)
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors';

// Registrar componentes do ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ExecutiveDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const chartsGridColor = useChartsColors();
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  /**
   * Carrega dados do dashboard
   */
  const loadDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('📊 Carregando Dashboard Executivo...', filters);

      const result = await ReportsService.getExecutiveDashboard({
        period: filters.period,
        user_type: filters.advancedFilters?.user_type,
        user_id: filters.advancedFilters?.user_id,
        product_id: filters.advancedFilters?.product_id,
        payment_method: filters.advancedFilters?.payment_method,
        sale_status: filters.advancedFilters?.sale_status,
        region: filters.advancedFilters?.region
      });

      if (result.success) {
        setDashboardData(result.data);
        console.log('✅ Dashboard carregado:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar dashboard');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar dashboard:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Carrega dashboard ao montar componente
   */
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  /**
   * Handle mudança de período
   */
  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => ({
      ...prev,
      period: newPeriod
    }));
  };

  /**
   * Handle filtros avançados
   */
  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        advancedFilters
      };
      // Recarrega imediatamente com os novos filtros
      setTimeout(() => {
        loadDashboardWithFilters(newFilters);
      }, 50);
      return newFilters;
    });
  };

  /**
   * Handle exportar CSV
   */
  const handleExport = async () => {
    try {
      const blob = await ReportsService.exportCSV('executive', filters);
      ReportsService.downloadCSV(blob, `dashboard-executivo-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('❌ Erro ao exportar CSV:', err);
      alert('Erro ao exportar CSV. Esta funcionalidade será implementada em breve.');
    }
  };

  /**
   * Handle mudança de filtros de usuário
   */
  const handleUserFilterChange = (userFilters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        advancedFilters: {
          ...prev.advancedFilters,
          user_type: userFilters.user_type || undefined,
          user_id: userFilters.user_id || undefined
        }
      };
      // Recarrega imediatamente com os novos filtros
      setTimeout(() => {
        loadDashboardWithFilters(newFilters);
      }, 50);
      return newFilters;
    });
  };

  /**
   * Carrega dashboard com filtros específicos (usado quando filtros mudam)
   */
  const loadDashboardWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('📊 Carregando Dashboard Executivo com filtros...', filtersToUse);

      const result = await ReportsService.getExecutiveDashboard({
        period: filtersToUse.period,
        user_type: filtersToUse.advancedFilters?.user_type,
        user_id: filtersToUse.advancedFilters?.user_id,
        product_id: filtersToUse.advancedFilters?.product_id,
        payment_method: filtersToUse.advancedFilters?.payment_method,
        sale_status: filtersToUse.advancedFilters?.sale_status,
        region: filtersToUse.advancedFilters?.region
      });

      if (result.success) {
        setDashboardData(result.data);
        console.log('✅ Dashboard carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar dashboard');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar dashboard:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Configuração do gráfico de tendência de vendas
   */
  const salesTrendChart = dashboardData?.charts?.salesTrend ? {
    data: {
      labels: dashboardData.charts.salesTrend.map(item => 
        new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
      ),
      datasets: [
        {
          label: 'Receita (R$)',
          data: dashboardData.charts.salesTrend.map(item => item.revenue / 100),
          borderColor: '#007bff',
          backgroundColor: 'rgba(0, 123, 255, 0.1)',
          fill: true,
          tension: 0.4,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => `R$ ${context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        }
      },
      scales: {
        x: {
          grid:{
             color: chartsGridColor
          }
        },
        y: {
          beginAtZero: true,
          grid:{
            color: chartsGridColor
          },
          ticks: {
            callback: (value) => `R$ ${value.toLocaleString('pt-BR')}`
          }
        }
      }
    }
  } : null;

  /**
   * Configuração do gráfico de top produtos
   */
  const topProductsChart = dashboardData?.charts?.topProducts ? {
    data: {
      labels: dashboardData.charts.topProducts.map(item => item.product_name),
      datasets: [
        {
          label: 'Receita (R$)',
          data: dashboardData.charts.topProducts.map(item => item.revenue / 100),
          backgroundColor: [
            '#007bff',
            '#28a745',
            '#ffc107',
            '#dc3545',
            '#17a2b8'
          ],
          borderWidth: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => `R$ ${context.parsed.x.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        }
      },
      scales: {
        y: {
          grid:{
            color: chartsGridColor
          },
        },
        x: {
          grid:{
            color: chartsGridColor
          },
          beginAtZero: true,
          ticks: {
            callback: (value) => `R$ ${value.toLocaleString('pt-BR')}`
          }
        }
      }
    }
  } : null;

  /**
   * Configuração do gráfico de comissões
   */
  const commissionsChart = dashboardData?.charts?.commissionsBreakdown ? {
    data: {
      labels: dashboardData.charts.commissionsBreakdown.map(item => item.role_name),
      datasets: [
        {
          data: dashboardData.charts.commissionsBreakdown.map(item => item.total_amount / 100),
          backgroundColor: [
            '#007bff',
            '#28a745',
            '#ffc107',
            '#dc3545',
            '#17a2b8',
            '#6610f2'
          ],
          borderWidth: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: (context) => `${context.label}: R$ ${context.parsed.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        }
      }
    }
  } : null;

  /**
   * Colunas da tabela de vendas recentes
   */
  const recentSalesColumns = [
    { key: 'sale_id', label: 'ID', sortable: true },
    { key: 'date', label: 'Data', format: 'datetime', sortable: true },
    { key: 'product_name', label: 'Produto', sortable: true },
    { key: 'customer_name', label: 'Cliente', sortable: true },
    { key: 'amount', label: 'Valor', format: 'currency', sortable: true },
    { 
      key: 'payment_status', 
      label: 'Status',
      format: 'badge',
      sortable: true,
      render: (value) => {
        const colors = {
          paid: 'success',
          pending: 'warning',
          cancelled: 'danger',
          refunded: 'info'
        };
        const labels = {
          paid: 'Pago',
          pending: 'Pendente',
          cancelled: 'Cancelado',
          refunded: 'Reembolsado'
        };
        return <span className={`badge bg-${colors[value] || 'secondary'}`}>{labels[value] || value}</span>;
      }
    }
  ];

  return (
    <MasterLayout>
      <Container fluid style={{ padding: '32px 24px', minHeight: '100vh' }}>
      {/* Cabeçalho com Botões */}
      <div style={{
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #e9ecef',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ flex: '1', minWidth: '300px' }}>
          <div style={{
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon icon="mdi:view-dashboard" style={{ color: '#007bff', fontSize: '22px' }} />
            <span style={{
              fontSize: '22px',
              fontWeight: '700',
              // color: '#212529'
            }} className="text-contrast">
              Dashboard Executivo
            </span>
          </div>
          <p style={{
            fontSize: '16px',
            color: '#6c757d',
            margin: 0
          }}>
            Visão 360° do seu negócio em tempo real
          </p>
        </div>
        
        {/* Botões de Ação */}
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          {/* Botão Filtros Avançados */}
          <Button
            variant={Object.keys(filters.advancedFilters || {}).filter(k => k !== 'user_type' && k !== 'user_id' && filters.advancedFilters[k]).length > 0 ? 'primary' : 'outline-primary'}
            onClick={() => setShowFilterDrawer(true)}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon icon="mdi:filter" style={{ fontSize: '18px' }} />
            Filtros Avançados
          </Button>

          {/* Botão Exportar */}
          <Button
            variant="outline-success"
            onClick={handleExport}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon icon="mdi:file-excel" style={{ fontSize: '18px' }} />
            Exportar CSV
          </Button>

          {/* Botão Atualizar */}
          <Button
            variant="primary"
            onClick={loadDashboard}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon 
              icon="mdi:refresh" 
              style={{ fontSize: '18px' }}
              className={loading ? 'spin' : ''}
            />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <FilterBar
        period={filters.period}
        onPeriodChange={handlePeriodChange}
        onApplyAdvancedFilters={handleAdvancedFilters}
        onRefresh={loadDashboard}
        onExport={handleExport}
        loading={loading}
        advancedFilters={filters.advancedFilters}
        onUserFilterChange={handleUserFilterChange}
        showExport={false}
      />

      {/* Drawer de Filtros Avançados */}
      <FilterDrawer
        show={showFilterDrawer}
        onHide={() => setShowFilterDrawer(false)}
        onApply={(newFilters) => {
          setShowFilterDrawer(false);
          handleAdvancedFilters(newFilters);
        }}
        currentFilters={filters.advancedFilters}
      />

      {/* Erro */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
          {error}
        </Alert>
      )}

      {/* Divider */}
      <hr style={{
        marginTop: '32px',
        marginBottom: '32px',
        border: 'none',
        borderTop: '2px solid #dee2e6'
      }} />

      {/* KPIs */}
      <Row className="g-4 mb-5">
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Vendas Totais"
            value={loading ? '...' : (dashboardData?.kpis?.total_sales?.value || 0).toLocaleString('pt-BR')}
            subtitle={loading ? '' : dashboardData?.kpis?.total_sales?.growth ? 
              `${dashboardData.kpis.total_sales.growth > 0 ? '↗' : '↘'} ${Math.abs(dashboardData.kpis.total_sales.growth).toFixed(1)}% vs período anterior` : 
              'Sem mudança vs período anterior'}
            icon="mdi:cart"
            variant="blue"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Receita Total"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(dashboardData?.kpis?.total_revenue?.value || 0)}
            subtitle={loading ? '' : dashboardData?.kpis?.total_revenue?.growth ? 
              `${dashboardData.kpis.total_revenue.growth > 0 ? '↗' : '↘'} ${Math.abs(dashboardData.kpis.total_revenue.growth).toFixed(1)}% vs período anterior` : 
              'Sem mudança vs período anterior'}
            icon="mdi:cash"
            variant="green"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Ticket Médio"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(dashboardData?.kpis?.avg_ticket?.value || 0)}
            subtitle={loading ? '' : dashboardData?.kpis?.avg_ticket?.growth ? 
              `${dashboardData.kpis.avg_ticket.growth > 0 ? '↗' : '↘'} ${Math.abs(dashboardData.kpis.avg_ticket.growth).toFixed(1)}% vs período anterior` : 
              'Sem mudança vs período anterior'}
            icon="mdi:chart-line"
            variant="orange"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Clientes Únicos"
            value={loading ? '...' : (dashboardData?.kpis?.unique_customers?.value || 0).toLocaleString('pt-BR')}
            subtitle={loading ? '' : dashboardData?.kpis?.unique_customers?.growth ? 
              `${dashboardData.kpis.unique_customers.growth > 0 ? '↗' : '↘'} ${Math.abs(dashboardData.kpis.unique_customers.growth).toFixed(1)}% vs período anterior` : 
              'Sem mudança vs período anterior'}
            icon="mdi:account-group"
            variant="purple"
          />
        </Col>
      </Row>

      {/* Gráficos */}
      <Row className="g-4 mb-5">
        {/* Tendência de Vendas */}
        <Col xs={12} lg={8}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '400px'
          }}>
            <Card.Body>
              <div style={{
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:chart-line" style={{ color: '#007bff', fontSize: '24px' }} />
                <span className='text-contrast' style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  // color: '#212529'
                }}>
                  Tendência de Receita
                </span>
              </div>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : salesTrendChart ? (
                <div style={{ height: '300px' }}>
                  <Line data={salesTrendChart.data} options={salesTrendChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Comissões */}
        <Col xs={12} lg={4}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '400px'
          }}>
            <Card.Body>
              <div style={{
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:account-cash" style={{ color: '#28a745', fontSize: '24px' }} />
                <span className='text-contrast' style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  // color: '#212529'
                }}>
                  Comissões
                </span>
              </div>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : commissionsChart ? (
                <div style={{ height: '300px' }}>
                  <Doughnut data={commissionsChart.data} options={commissionsChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Produtos */}
      <Row className="g-4 mb-5">
        <Col xs={12}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '400px'
          }}>
            <Card.Body>
              <div style={{
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:trophy" style={{ color: '#ffc107', fontSize: '24px' }} />
                <span className='text-contrast' style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  // color: '#212529'
                }}>
                  Top 5 Produtos
                </span>
              </div>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : topProductsChart ? (
                <div style={{ height: '300px' }}>
                  <Bar data={topProductsChart.data} options={topProductsChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Vendas Recentes */}
      <Row>
        <Col xs={12}>
          <div style={{
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icon icon="mdi:clock-outline" style={{ color: '#6c757d', fontSize: '24px' }} />
            <span className='text-contrast' style={{
              fontSize: '18px',
              fontWeight: '600',
              // color: '#212529'
            }}>
              Vendas Recentes
            </span>
          </div>
          <SmartTable
            data={dashboardData?.recentSales || []}
            columns={recentSalesColumns}
            loading={loading}
            searchable={true}
            itemsPerPage={10}
            emptyMessage="Nenhuma venda encontrada"
          />
        </Col>
      </Row>
      </Container>
    </MasterLayout>
  );
};

export default ExecutiveDashboard;

